import React, { useEffect, useState } from "react";

import { categoriesService } from "api/services/categories.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";

import type { Category, CategoryChildInput } from "api/types/category.types";
import "pages/AdminPage.scss";
import "./CategoryManagement.scss";

type CategoryType = "parent" | "child";

interface ChildRow extends CategoryChildInput {
  _key: number;
}

const CategoryManagement: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [categoryType, setCategoryType] = useState<CategoryType>("parent");
  const [form, setForm] = useState({ name: "", slug: "", parent: "" });
  const [childRows, setChildRows] = useState<ChildRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugError, setSlugError] = useState("");
  const [childSlugErrors, setChildSlugErrors] = useState<Record<number, string>>({});

  const SLUG_RE = /^[a-z0-9-]*$/;

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await categoriesService.listAll();
      const data = res.data;
      const parents: Category[] = Array.isArray(data) ? data : (data.results ?? []);
      // 중첩 children을 평탄화
      const flat: Category[] = [];
      for (const parent of parents) {
        flat.push(parent);
        if (parent.children?.length) flat.push(...parent.children);
      }
      setCategories(flat);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const parentCategories = categories.filter((c) => c.depth === 0);

  /* ───────── 슬러그 자동 생성 ───────── */
  const toSlug = (name: string) =>
    name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  /* ───────── 모달 열기 ───────── */
  const openCreateModal = () => {
    setEditTarget(null);
    setCategoryType("parent");
    setForm({ name: "", slug: "", parent: "" });
    setChildRows([]);
    setSlugError("");
    setChildSlugErrors({});
    setShowModal(true);
  };

  const openEditModal = (cat: Category) => {
    setEditTarget(cat);
    setCategoryType(cat.depth === 0 ? "parent" : "child");
    setForm({
      name: cat.name,
      slug: cat.slug,
      parent: cat.parent ? String(cat.parent) : "",
    });
    setChildRows([]);
    setSlugError("");
    setChildSlugErrors({});
    setShowModal(true);
  };

  /* ───────── 소분류 행 관리 ───────── */
  let _keyCounter = 0;
  const nextKey = () => ++_keyCounter;

  const addChildRow = () => setChildRows((prev) => [...prev, { _key: Date.now() + nextKey(), name: "", slug: "" }]);

  const updateChildRow = (key: number, field: keyof CategoryChildInput, value: string) => {
    setChildRows((prev) =>
      prev.map((r) => {
        if (r._key !== key) return r;
        const updated = { ...r, [field]: value };
        if (field === "name" && !r.slug) updated.slug = toSlug(value);
        return updated;
      }),
    );
    if (field === "slug") {
      setChildSlugErrors((prev) => ({
        ...prev,
        [key]: SLUG_RE.test(value) ? "" : "영문, 숫자, 하이픈만 사용 가능합니다.",
      }));
    }
  };

  const removeChildRow = (key: number) => setChildRows((prev) => prev.filter((r) => r._key !== key));

  /* ───────── 이름 → 슬러그 자동 채우기 ───────── */
  const handleNameChange = (value: string) => {
    setForm((p) => ({ ...p, name: value, slug: p.slug || toSlug(value) }));
  };

  const handleSlugChange = (value: string) => {
    setForm((p) => ({ ...p, slug: value }));
    setSlugError(SLUG_RE.test(value) ? "" : "영문, 숫자, 하이픈만 사용 가능합니다.");
  };

  /* ───────── 삭제 ───────── */
  const handleDelete = async (id: number) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    await categoriesService.remove(id);
    fetchCategories();
  };

  /* ───────── 저장 ───────── */
  const handleSave = async () => {
    if (!form.name) return;
    if (slugError) return;
    if (Object.values(childSlugErrors).some((e) => e)) return;

    setSaving(true);
    try {
      if (editTarget) {
        const payload: Record<string, unknown> = {
          name: form.name,
          slug: form.slug || undefined,
        };
        if (categoryType === "child") payload.parent = form.parent ? Number(form.parent) : undefined;
        await categoriesService.update(editTarget.id, payload);
      } else if (categoryType === "parent") {
        const validChildren = childRows.filter((r) => r.name.trim());
        await categoriesService.create({
          name: form.name,
          slug: form.slug || undefined,
          depth: 0,
          children: validChildren.length ? validChildren.map(({ name, slug }) => ({ name, slug })) : undefined,
        });
      } else {
        // 소분류만 생성
        await categoriesService.create({
          name: form.name,
          slug: form.slug || undefined,
          parent: form.parent ? Number(form.parent) : undefined,
          depth: 1,
        });
      }
      setShowModal(false);
      fetchCategories();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  /* ───────── 트리 정렬 ───────── */
  const treeRows: Category[] = [];
  for (const parent of parentCategories) {
    treeRows.push(parent);
    treeRows.push(...categories.filter((c) => c.parent === parent.id));
  }
  const inTree = new Set(treeRows.map((c) => c.id));
  categories.forEach((c) => {
    if (!inTree.has(c.id)) treeRows.push(c);
  });

  /* ───────── 테이블 컬럼 ───────── */
  const columns: Column<Category>[] = [
    {
      key: "depth",
      title: "분류",
      width: "100px",
      render: (v) => (v === 0 ? <strong style={{ color: "#18181B" }}>대분류</strong> : <span style={{ color: "#71717A" }}>└ 소분류</span>),
    },
    {
      key: "name",
      title: "카테고리명",
      render: (v) => <span>{String(v)}</span>,
    },
    { key: "slug", title: "Slug", width: "200px" },
    {
      key: "is_active",
      title: "공개여부",
      width: "90px",
      render: (v) => <Badge variant={v ? "active" : "draft"}>{v ? "공개" : "숨김"}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      // width: "140px",
      render: (_, row) => (
        <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
          {/* <Button size="sm" variant="ghost" onClick={() => handleToggleVisible(row)}>
            {row.is_active ? "숨기기" : "공개"}
          </Button> */}
          <Button size="sm" variant="outline" onClick={() => handleDelete(row.id)}>
            삭제
          </Button>
        </div>
      ),
    },
  ];

  /* ───────── 렌더링 ───────── */
  return (
    <div className="admin-page">
      <ContentHeader
        title="카테고리 목록"
        description="상품 카테고리를 관리합니다. 대분류/소분류 구조로 이루어져 있습니다."
        actions={[
          <Button key="add" variant="primary" onClick={openCreateModal}>
            + 카테고리 추가
          </Button>,
        ]}
      />

      <div className="admin-page__card">{loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={treeRows} rowKey="id" onRowClick={openEditModal} />}</div>

      <Modal
        open={showModal}
        title={editTarget ? "카테고리 수정" : "카테고리 추가"}
        onClose={() => setShowModal(false)}
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving || !!slugError || Object.values(childSlugErrors).some((e) => e)}>
              {saving ? "저장 중..." : editTarget ? "수정" : "추가"}
            </Button>
          </>
        }
      >
        <div className="category-form">
          {/* 분류 유형 선택 — 신규 생성 시만 표시 */}
          {!editTarget && (
            <div className="category-form__type-tabs">
              <button type="button" className={`category-form__type-tab${categoryType === "parent" ? " --active" : ""}`} onClick={() => setCategoryType("parent")}>
                대분류 생성
              </button>
              <button type="button" className={`category-form__type-tab${categoryType === "child" ? " --active" : ""}`} onClick={() => setCategoryType("child")}>
                소분류 생성
              </button>
            </div>
          )}

          {/* 소분류 생성: 상위 카테고리 선택 */}
          {(categoryType === "child" || (editTarget && editTarget.depth === 1)) && (
            <Select
              label="상위 카테고리 *"
              value={form.parent}
              onChange={(e) => setForm((p) => ({ ...p, parent: e.target.value }))}
              options={[
                { value: "", label: "선택하세요" },
                ...parentCategories.map((c) => ({
                  value: String(c.id),
                  label: c.name,
                })),
              ]}
            />
          )}

          {/* 카테고리명 */}
          <Input label="카테고리명 *" placeholder="예: 다이빙 장비" value={form.name} onChange={(e) => handleNameChange(e.target.value)} />

          {/* Slug */}
          <div>
            <Input label="Slug" placeholder="예: diving-equipment" value={form.slug} onChange={(e) => handleSlugChange(e.target.value)} />
            {slugError ? <p className="category-form__field-error">{slugError}</p> : <p className="category-form__field-hint">영문, 숫자, 하이픈만 사용 가능합니다. 비워두면 자동 생성됩니다.</p>}
          </div>

          {/* 소분류 동시 추가 — 대분류 신규 생성 시만 */}
          {!editTarget && categoryType === "parent" && (
            <div className="category-form__children">
              <div className="category-form__children-header">
                <span className="category-form__children-label">소분류 (선택)</span>
                <Button size="sm" variant="ghost" type="button" onClick={addChildRow}>
                  + 소분류 추가
                </Button>
              </div>

              {childRows.length === 0 && <p className="category-form__children-empty">소분류가 없습니다. 대분류만 생성하거나 위 버튼으로 소분류를 추가하세요.</p>}

              {childRows.map((row, idx) => (
                <div key={row._key} className="category-form__child-row">
                  <span className="category-form__child-index">{idx + 1}</span>
                  <div className="category-form__child-fields">
                    <Input label="소분류명" placeholder="예: 핀" value={row.name} onChange={(e) => updateChildRow(row._key, "name", e.target.value)} />
                    <div>
                      <Input label="Slug" placeholder="예: fins" value={row.slug} onChange={(e) => updateChildRow(row._key, "slug", e.target.value)} />
                      {childSlugErrors[row._key] && <p className="category-form__field-error">{childSlugErrors[row._key]}</p>}
                    </div>
                  </div>
                  <button type="button" className="category-form__child-remove" onClick={() => removeChildRow(row._key)} aria-label="소분류 삭제">
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CategoryManagement;
